/*
 * Copyright © 2015-2022 Serenova, LLC. All rights reserved.
 */

import React, { Component, Fragment } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LoadingSpinnerSVG, Logo, LanguagePicker } from 'cx-ui-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { mappedLocalesOptions } from '../../i18n';
import { cxChangePassword, cxGetUserName, cxInit, versionCheck } from '../../utils/cxSdk';
import { Toast } from 'cx-ui-components';
import { isEmpty } from 'serenova-js-utils/strings';

import PrivacyPolicy from '../Legal/privacyPolicy';
import LegalPolicy from '../Legal/legalPolicy';
import messages from './messages';

const Loading = styled(LoadingSpinnerSVG)`
  top: 150px;
  top: calc(50% - ${(props) => props.size / 2}px);
  left: calc(50% - ${(props) => props.size / 2}px);
`;

const TempBackground = styled.div`
  position: fixed;
  top: 0px;
  width: 100%;
  height: 100%;
  background-repeat: repeat;
  background-image: url('https://qe-config.cxengagelabs.net/assets/images/square_bg.png'),
    linear-gradient(to bottom, #0a64aa, #3498db);
  z-index: -1;
`;

const SignInTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  color: rgb(73, 73, 73);
  display: block;
  text-align: center;
  margin: 30px 0px;
`;

const LoginFormWrapper = styled.div`
  position: relative;
  top: 70px;
`;

const LoginBox = styled.div`
  width: 540px;
  height: 560px;
  margin: 0px auto;
  margin-top: calc(50vh - 270px);
  border-radius: 3px;
  background-color: rgb(255, 255, 255);
  box-shadow: rgba(0, 0, 0, 0.09) 0px 0px 6px 0px;
  position: relative;
  z-index: 1;
`;

const PasswordInput = styled.input`
  width: 282px;
  height: 44px;
  background-color: rgb(255, 255, 255);
  font-size: 16px;
  font-style: normal;
  font-stretch: normal;
  color: rgb(73, 73, 73);
  outline: none;
  padding: 0px 11px;
  border-radius: 2px;
  border-width: 1px;
  border-style: solid;
  border-color: rgb(151, 151, 151);
  margin-bottom: 10px;
  display: block;
  margin: 10px auto;
`;

const ChangePasswordButton = styled.button`
  border-radius: 8px;
  border-width: 0px;
  background-color: rgb(35, 205, 244);
  font-size: 16px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  color: rgb(255, 255, 255);
  padding: 14px 28px;
  cursor: pointer;
  outline: none;
  display: block;
  margin: 10px auto;
`;

const StyledLanguagePicker = styled(LanguagePicker)`
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 1.5em;
`;

const PasswordPolicyText = styled.div`
  text-align: center;
`;

const storage = window.localStorage;

export class ResetPassword extends Component {
  constructor() {
    super();
    this.state = {
      confirmPassword: '',
      newPassword: '',
      redirect: false,
      sdkLoaded: false,
    };
  }

  componentDidMount() {
    cxInit();

    // validate if page has loaded completely
    addEventListener('load', this.handleLoadEvent);

    if (this.props.getUserName && this.props.getUserName === 'no-username') {
      this.setState({ redirect: true });
    }
  }

  componentWillUnmount() {
    clearInterval(versionCheck);
    removeEventListener('load', this.handleLoadEvent);
    window.removeEventListener('message', this.handleMessageEvent);
  }

  componentDidUpdate(prevProps) {
    if ((this.props.getUserName && this.props.getUserName === 'no-username') ||
      (prevProps.getUserName === 'no-username' && this.props.getUserName === 'no-username')) {
      this.setState({ redirect: true });
    }
  }

  handleLoadEvent = () => {
    // constantly check if sdk has finished initialization
    window.addEventListener('message', this.handleMessageEvent);
  };

  handleMessageEvent = () => {
    if (CxEngage.version && !this.state.sdkLoaded) {
      this.setState({ sdkLoaded: true });

      if (CxEngage.version) {
        cxGetUserName();
      }
    }

    if (CxEngage.version && this.state.sdkLoaded) {
      if (this.props.getUserName && this.props.getUserName === 'no-username') {
        this.setState({ redirect: true });
      }
    }
  };

  changePassword = () => {
    //if validate password
    // Check length
    // Must be at least 8 characters in length
    // Check for alphabetic characters
    // Must contain at least 1 alphabetic characters
    // Check for numeric case characters
    // Must contain at least 1 numeric case characters
    // Check for special characters
    // Must contain at least 1 characters from !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
    if (isEmpty(this.state.newPassword) || isEmpty(this.state.confirmPassword)) {
      Toast.error(this.props.intl.formatMessage(messages.missingPassword));
    } else if (this.state.newPassword.length < 8 && this.state.confirmPassword.length < 8) {
      Toast.error(this.props.intl.formatMessage(messages.passwordLength));
    } else if (this.state.newPassword !== this.state.confirmPassword) {
      Toast.error(this.props.intl.formatMessage(messages.mixMatch));
    } else if (
      !/.*(?=.*[a-zA-Z])(?=.*\d)(?=.*[\!\#\$\%\-\_\=\+\<\>\.]).*/.test(this.state.newPassword) ||
      !/.*(?=.*[a-zA-Z])(?=.*\d)(?=.*[\!\#\$\%\-\_\=\+\<\>\.]).*/.test(this.state.confirmPassword)
    ) {
      Toast.error(this.props.intl.formatMessage(messages.passwordPolicyText));
    } else {
      if (cxChangePassword(this.state.confirmPassword)) {
        Toast.success(this.props.intl.formatMessage(messages.passwordUpdateSuccess));
        this.setState({ redirect: true });
      } else {
        Toast.error(this.props.intl.formatMessage(messages.passwordUpdateError));
      }
    }
  };

  setNewPassword = (e) => {
    this.setState({
      newPassword: e.target.value,
    });
  };

  setConfirmPassword = (e) => {
    this.setState({
      confirmPassword: e.target.value,
    });
  };

  changeLocale = (locale) => {
    storage.setItem('locale', locale);
    this.props.changeLocale(locale);
  };

  render() {
    let userName = ' "' + this.props.getUserName + '"';
    if (!this.props.insideIframe && !this.props.hasStarted) {
      return (
        <Fragment>
          {this.state.redirect==true? <Redirect to="/" />:null }
          <LoginBox>
            <LoginFormWrapper>
              <Logo />
              <div>
                <SignInTitle>
                  <FormattedMessage {...messages.welcome} />
                  {userName}
                </SignInTitle>
                <PasswordPolicyText>
                  <FormattedMessage {...messages.passwordPolicyText} />
                </PasswordPolicyText>
                <PasswordInput
                  data-automation="newPassword"
                  placeholder={this.props.intl.formatMessage(messages.newPassword)}
                  type="password"
                  onChange={this.setNewPassword}
                  value={this.state.newPassword}
                />
                <PasswordInput
                  data-automation="confirmPassword"
                  placeholder={this.props.intl.formatMessage(messages.confirmPassword)}
                  type="password"
                  onChange={this.setConfirmPassword}
                  value={this.state.confirmPassword}
                />
                <ChangePasswordButton
                  data-automation="changePasswordButton"
                  onClick={() => this.changePassword(this.state.newPassword)}>
                  <FormattedMessage {...messages.changePassword} />
                </ChangePasswordButton>
              </div>
            </LoginFormWrapper>
          </LoginBox>
          {location.hash.includes('alpha') && (
            <StyledLanguagePicker
              color="#fff"
              onLanguageChange={this.changeLocale}
              selectedLocale={this.props.locale}
              languageOptions={mappedLocalesOptions}
            />
          )}
          <LegalPolicy />
          <PrivacyPolicy />
          <TempBackground />
        </Fragment>
      );
    } else if (!this.props.insideIframe && this.props.hasStarted) {
      return <Redirect to="/configuration/users" />;
    } else {
      return <Loading size={120} />;
    }
  }
}

export default injectIntl(ResetPassword);

ResetPassword.propTypes = {
  hasStarted: PropTypes.bool.isRequired,
  insideIframe: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  changeLocale: PropTypes.func.isRequired,
  getUserName: PropTypes.string,
};
